import { Image as ReactNativeImage, PixelRatio, Platform } from "react-native";

// React Native's `AssetSourceResolver.assetServerURL()` builds the dev-server
// URL by concatenating `httpServerLocation + '/' + name + '.' + type + '?...'`.
// For monorepo-resolved assets, Metro emits an `httpServerLocation` that
// *already* contains `?unstable_path=...`, so the final URL ends up with two
// `?`s — the second one nests the file name inside the value of `unstable_path`
// and appends `&platform=...&hash=...` as top-level query params. For ASCII
// filenames this happens to still resolve because the path that survives
// after the first `?` is well-formed enough for Metro's router. For non-ASCII
// filenames (e.g. `一.png`), the raw bytes inside the URL break the path
// matcher and Metro returns HTTP 400 — silently, since RN's iOS `Image` swallows
// load errors.
//
// `resolveAssetSource.addCustomSourceTransformer` is RN's official extension
// point for overriding asset URL construction. We intercept any asset whose
// `httpServerLocation` already carries `?unstable_path=...` and rewrite the
// URL so the filename is appended to the `unstable_path` value (URL-encoded)
// and `platform` / `hash` are merged in with `&`. Asset bundles in release
// builds don't hit this branch — `isLoadedFromServer()` is `false` when there
// is no dev server — so the patch only matters in dev / Metro-served scenarios.

const HTTP_SERVER_LOCATION_WITH_QUERY_REGEX = /^([^?]+)\?(.*)$/;

type Asset = {
  hash: string;
  httpServerLocation: string;
  name: string;
  scales: readonly number[];
  type: string;
};

type AssetSourceFromSource = {
  __packager_asset: boolean;
  height: number | null;
  scale: number;
  uri: string;
  width: number | null;
};

type AssetSourceResolver = {
  asset: Asset;
  fromSource: (uri: string) => AssetSourceFromSource;
  isLoadedFromServer: () => boolean;
  serverUrl: string | null | undefined;
};

let applied = false;

function pickScale(scales: readonly number[], deviceScale: number): number {
  for (const scale of scales) {
    if (scale >= deviceScale) {
      return scale;
    }
  }
  return scales.at(-1) ?? 1;
}

function buildAssetServerURL(serverUrl: string, asset: Asset): string {
  const scale = pickScale(asset.scales, PixelRatio.get());
  const scaleSuffix = scale === 1 ? "" : `@${scale}x`;
  const filename = `${asset.name}${scaleSuffix}.${asset.type}`;
  const trimmedServerUrl = serverUrl.endsWith("/") ? serverUrl.slice(0, -1) : serverUrl;
  const locationPath = asset.httpServerLocation.startsWith("/")
    ? asset.httpServerLocation
    : `/${asset.httpServerLocation}`;

  const queryMatch = HTTP_SERVER_LOCATION_WITH_QUERY_REGEX.exec(locationPath);
  if (queryMatch === null) {
    // No pre-existing query in httpServerLocation — let RN's default builder
    // handle it. Returning null up the stack signals "use default".
    return `${trimmedServerUrl}${locationPath}/${encodeURIComponent(filename)}?platform=${Platform.OS}&hash=${encodeURIComponent(asset.hash)}`;
  }

  // Append the filename onto whatever path is encoded inside `unstable_path` (or
  // similar) — Metro decodes that value and treats it as the on-disk asset dir.
  const [, basePath, baseQuery] = queryMatch;
  const params = new URLSearchParams(baseQuery);
  const unstablePath = params.get("unstable_path");
  if (unstablePath !== null) {
    params.set("unstable_path", `${unstablePath}/${filename}`);
  }
  params.set("platform", Platform.OS);
  params.set("hash", asset.hash);
  return `${trimmedServerUrl}${basePath}?${params.toString()}`;
}

const resolveAssetSource = ReactNativeImage.resolveAssetSource;

/**
 * Installs the asset-source transformer once. Idempotent so Fast Refresh
 * doesn't stack duplicate transformers.
 */
export function applyAssetSourcePatch(): void {
  if (applied) {
    return;
  }
  applied = true;

  // RN-Web (and SSR) doesn't expose `Image.resolveAssetSource` — the
  // unstable_path URL bug this patch addresses is iOS/Android-only, so there's
  // nothing to do on web. `Reflect.get` on `undefined` throws, so guard the
  // object check first instead of letting SSR die with a stackless TypeError.
  if (typeof resolveAssetSource !== "object" && typeof resolveAssetSource !== "function") {
    return;
  }
  if (resolveAssetSource === null) {
    return;
  }
  const addCustomSourceTransformer = Reflect.get(resolveAssetSource, "addCustomSourceTransformer");
  if (typeof addCustomSourceTransformer !== "function") {
    return;
  }

  addCustomSourceTransformer((resolver: AssetSourceResolver) => {
    if (!resolver.isLoadedFromServer() || resolver.serverUrl == null) {
      return null;
    }
    const asset = resolver.asset;
    if (!asset.httpServerLocation.includes("?")) {
      return null;
    }
    const uri = buildAssetServerURL(resolver.serverUrl, asset);
    return resolver.fromSource(uri);
  });
}
